import { saveAs } from 'file-saver';

/** Κατάσταση επεξεργασίας μιας γραμμής Excel */
export type RowResultStatus = 'success' | 'updated' | 'error' | 'skipped';

export type RowImportResult = {
  /**
   * Δείκτης στο sheet._rows (0-based), όπως τον χρησιμοποιεί το import loop.
   * Το ExcelJS getRow() περιμένει 1-based αριθμό γραμμής Excel (= index + 1).
   */
  sheetRowIndex: number;
  status: RowResultStatus;
  /** ID νέου ή ενημερωμένου σημείου συλλογής */
  collectionPointId?: number;
  /** Ανθρώπινο μήνυμα αποτελέσματος */
  message: string;
};

export const RESULT_STATUS_HEADER = 'Κατάσταση Εισαγωγής';
export const RESULT_DETAIL_HEADER = 'Αποτέλεσμα Εισαγωγής';

const STATUS_LABEL: Record<RowResultStatus, string> = {
  success: 'ΕΠΙΤΥΧΙΑ',
  updated: 'ΕΝΗΜΕΡΩΘΗΚΕ',
  error: 'ΣΦΑΛΜΑ',
  skipped: 'ΠΑΡΑΛΕΙΨΗ',
};

const STATUS_FILL: Record<RowResultStatus, string> = {
  success: 'FFD4EDDA',
  updated: 'FFD1ECF1',
  error: 'FFF8D7DA',
  skipped: 'FFE2E3E5',
};

const STATUS_FONT: Record<RowResultStatus, string> = {
  success: 'FF155724',
  updated: 'FF0C5460',
  error: 'FF721C24',
  skipped: 'FF383D41',
};

function normalizeHeader(value: unknown): string {
  if (value == null) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/**
 * Μετατροπή δείκτη _rows → αριθμός γραμμής Excel (1-based).
 * Στο ExcelJS 4.x: getRow(n) διαβάζει _rows[n - 1].
 */
function toExcelRowNumber(sheet: any, rowsIndex: number): number {
  const packed = sheet._rows?.[rowsIndex];
  if (packed && typeof packed.number === 'number' && packed.number > 0) {
    return packed.number;
  }
  return rowsIndex + 1;
}

/** Τελευταία μη κενή στήλη της γραμμής επικεφαλίδας */
function getHeaderLastColumn(sheet: any, excelHeaderRow: number): number {
  const headerRow = sheet.getRow(excelHeaderRow);
  let max = 0;
  headerRow.eachCell({ includeEmpty: false }, (_cell: unknown, colNumber: number) => {
    max = Math.max(max, colNumber);
  });
  if (max === 0) {
    const cells = sheet._rows?.[excelHeaderRow - 1]?._cells;
    if (Array.isArray(cells)) {
      for (let i = 0; i < cells.length; i++) {
        if (cells[i] != null) max = Math.max(max, i + 1);
      }
    }
  }
  return Math.max(max, 1);
}

/** Βρίσκει υπάρχουσα στήλη αποτελέσματος ή επιστρέφει την επόμενη κενή στα δεξιά */
function resolveResultColumnIndexes(
  sheet: any,
  excelHeaderRow: number,
): {
  statusCol: number;
  detailCol: number;
} {
  const headerRow = sheet.getRow(excelHeaderRow);
  const lastCol = getHeaderLastColumn(sheet, excelHeaderRow);
  let statusCol = -1;
  let detailCol = -1;

  for (let c = 1; c <= lastCol; c++) {
    const label = normalizeHeader(headerRow.getCell(c).value);
    if (label === normalizeHeader(RESULT_STATUS_HEADER)) statusCol = c;
    if (label === normalizeHeader(RESULT_DETAIL_HEADER)) detailCol = c;
  }

  let nextCol = lastCol + 1;
  if (statusCol < 0) statusCol = nextCol++;
  if (detailCol < 0) {
    detailCol = nextCol;
    if (detailCol === statusCol) detailCol = statusCol + 1;
  }

  return { statusCol, detailCol };
}

function buildDetailText(result: RowImportResult): string {
  const parts: string[] = [];

  if (result.status === 'success' || result.status === 'updated') {
    if (result.collectionPointId != null) {
      parts.push(`ID Σημείου: ${result.collectionPointId}`);
    }
    if (result.message) parts.push(result.message);
  } else {
    parts.push(result.message);
  }

  return parts.filter(Boolean).join(' · ');
}

/**
 * Προσθέτει/ενημερώνει στήλες αποτελέσματος στο φύλλο εισαγωγής.
 * Δεν αγγίζει τις στήλες δεδομένων· γράφει μόνο Κατάσταση + Αποτέλεσμα.
 */
export function annotateWorksheetWithResults(
  sheet: any,
  headerRowIdx: number,
  results: RowImportResult[],
): void {
  if (!results.length) return;

  const excelHeaderRow = toExcelRowNumber(sheet, headerRowIdx);
  const { statusCol, detailCol } = resolveResultColumnIndexes(sheet, excelHeaderRow);
  const headerRow = sheet.getRow(excelHeaderRow);

  const statusHeader = headerRow.getCell(statusCol);
  statusHeader.value = RESULT_STATUS_HEADER;
  statusHeader.font = { bold: true };
  statusHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

  const detailHeader = headerRow.getCell(detailCol);
  detailHeader.value = RESULT_DETAIL_HEADER;
  detailHeader.font = { bold: true };
  detailHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

  sheet.getColumn(statusCol).width = 18;
  sheet.getColumn(detailCol).width = 56;

  for (const result of results) {
    const excelRow = toExcelRowNumber(sheet, result.sheetRowIndex);
    const row = sheet.getRow(excelRow);
    const statusCell = row.getCell(statusCol);
    const detailCell = row.getCell(detailCol);

    statusCell.value = STATUS_LABEL[result.status];
    statusCell.font = { bold: true, color: { argb: STATUS_FONT[result.status] } };
    statusCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: STATUS_FILL[result.status] },
    };
    statusCell.alignment = { vertical: 'middle', horizontal: 'center' };

    detailCell.value = buildDetailText(result);
    detailCell.alignment = { vertical: 'middle', wrapText: true };
  }
}

/** Όνομα αρχείου αποτελέσματος με βάση το αρχικό */
export function buildResultFileName(originalName?: string): string {
  const base = (originalName || 'εισαγωγή-σημείων-συλλογής').replace(/\.xlsx$/i, '');
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `${base}_αποτέλεσμα_${stamp}.xlsx`;
}

/** Δημιουργεί Blob από workbook για αποθήκευση στο state (λήψη αργότερα) */
export async function workbookToResultBlob(workbook: any): Promise<Blob> {
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function downloadResultBlob(blob: Blob, fileName: string): void {
  saveAs(blob, fileName);
}
